import React, { useState, useMemo } from 'react'
import { designTokens } from '@/utils/design-tokens'

interface BudgetItem {
  id: string
  name: string
  budget: number | string | null
  execution: number | string | null
}

interface BudgetData {
  description: string
  items: BudgetItem[]
}

interface BudgetSectionProps {
  budgetData: BudgetData
  onChange: (budgetData: BudgetData) => void
  disabled?: boolean
}

const BudgetSection: React.FC<BudgetSectionProps> = ({
  budgetData,
  onChange,
  disabled = false
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [editingValues, setEditingValues] = useState<Record<string, string>>({})

  // Calculate totals (supports decimal numbers)
  const totals = useMemo(() => {
    const totalBudget = budgetData.items.reduce((sum, item) => {
      const budget = typeof item.budget === 'string' ? parseFloat(item.budget) || 0 : (item.budget || 0)
      return sum + budget
    }, 0)
    const totalExecution = budgetData.items.reduce((sum, item) => {
      const execution = typeof item.execution === 'string' ? parseFloat(item.execution) || 0 : (item.execution || 0)
      return sum + execution
    }, 0)
    const executionRate = totalBudget > 0 ? Math.round((totalExecution / totalBudget) * 10000) / 100 : 0

    return {
      budget: totalBudget,
      execution: totalExecution,
      rate: executionRate
    }
  }, [budgetData.items])

  // Format number with commas (supports decimal numbers)
  const formatNumber = (num: number | string) => {
    const numValue = typeof num === 'string' ? parseFloat(num) || 0 : num
    return new Intl.NumberFormat('ko-KR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(numValue)
  }

  // Handle description change
  const handleDescriptionChange = (description: string) => {
    onChange({
      ...budgetData,
      description
    })
  }

  // Handle adding new budget item
  const handleAddItem = () => {
    const newItem: BudgetItem = {
      id: `new-budget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: '',
      budget: null,
      execution: null
    }
    onChange({
      ...budgetData,
      items: [...budgetData.items, newItem]
    })
  }

  // Handle removing budget item
  const handleRemoveItem = (itemId: string) => {
    onChange({
      ...budgetData,
      items: budgetData.items.filter(item => item.id !== itemId)
    })
    // Clear any errors for removed item
    const newErrors = { ...errors }
    Object.keys(newErrors).forEach(key => {
      if (key.includes(itemId)) {
        delete newErrors[key]
      }
    })
    setErrors(newErrors)
  }

  // Handle item field changes
  const handleItemChange = (itemId: string, field: keyof BudgetItem, value: string | number | null) => {
    const updatedItems = budgetData.items.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    )
    onChange({
      ...budgetData,
      items: updatedItems
    })

    // Clear field-specific error
    const errorKey = `${itemId}_${field}`
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[errorKey]
        return newErrors
      })
    }
  }

  // Handle number input parsing (supports decimal numbers)
  const parseNumberInput = (value: string): number | null => {
    const cleanValue = value.replace(/,/g, '').trim()
    if (!cleanValue) return null
    const num = parseFloat(cleanValue)
    return isNaN(num) ? null : num
  }

  // Get display value for input (editing value or formatted value)
  const getDisplayValue = (itemId: string, field: 'budget' | 'execution', actualValue: number | string | null): string => {
    const editingKey = `${itemId}_${field}`
    if (editingValues[editingKey] !== undefined) {
      return editingValues[editingKey]
    }
    return actualValue ? formatNumber(actualValue) : ''
  }

  // Handle input focus (start editing)
  const handleInputFocus = (itemId: string, field: 'budget' | 'execution', actualValue: number | string | null) => {
    const editingKey = `${itemId}_${field}`
    const rawValue = actualValue ? String(actualValue) : ''
    setEditingValues(prev => ({ ...prev, [editingKey]: rawValue }))
  }

  // Handle input change (while editing)
  const handleInputChange = (itemId: string, field: 'budget' | 'execution', value: string) => {
    const editingKey = `${itemId}_${field}`
    setEditingValues(prev => ({ ...prev, [editingKey]: value }))
  }

  // Handle input blur (finish editing)
  const handleInputBlur = (itemId: string, field: 'budget' | 'execution', value: string) => {
    const editingKey = `${itemId}_${field}`
    const parsedValue = parseNumberInput(value)
    handleItemChange(itemId, field, parsedValue)

    // Clear editing state
    setEditingValues(prev => {
      const newEditingValues = { ...prev }
      delete newEditingValues[editingKey]
      return newEditingValues
    })
  }

  // Validate required fields (for future use)
  // const validateItem = (item: BudgetItem) => {
  //   const itemErrors: Record<string, string> = {}
  //   
  //   if (!item.name.trim()) {
  //     itemErrors[`${item.id}_name`] = '예산명을 입력해주세요.'
  //   }
  //   
  //   if (item.budget === null || item.budget <= 0) {
  //     itemErrors[`${item.id}_budget`] = '예산을 입력해주세요.'
  //   }
  //   
  //   return itemErrors
  // }

  return (
    <div className="space-y-2">
      {/* Section Header */}
      <div 
        className="pb-2 border-b" 
        style={{ 
          borderBottomColor: '#EBEBEB',
          borderBottomWidth: '1px',
          padding: '7px 0px'
        }}
      >
        <h3 
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '18px',
            fontWeight: 700,
            color: designTokens.colors.gray[6]
          }}
        >
          예산
        </h3>
      </div>

      {/* Content */}
      <div className="space-y-2" style={{ padding: '0px 4px' }}>
        {/* Budget Description */}
        <div className="flex items-center gap-5">
          <div className="w-37 flex-shrink-0">
            <span 
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '16px',
                fontWeight: 400,
                color: designTokens.colors.gray[6]
              }}
            >
              예산 산출 내역
            </span>
          </div>
          <div className="flex-1">
            <textarea
              value={budgetData.description}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              placeholder="내용을 입력해주세요. (100자 이내)"
              disabled={disabled}
              className="w-full p-3 border rounded resize-none"
              rows={3}
              maxLength={100}
              style={{
                borderColor: designTokens.colors.gray[1],
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '16px',
                color: designTokens.colors.gray[6],
                backgroundColor: disabled ? designTokens.colors.gray[0] : 'white'
              }}
            />
          </div>
        </div>

        {/* Budget Summary and Add Button */}
        <div className="space-y-3" style={{ padding: '4px 0px' }}>
          <div className="flex justify-center items-center gap-2.5">
            {/* Total Budget Label */}
            <div className="flex justify-center" style={{ width: '160px' }}>
              <span 
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  fontWeight: 400,
                  color: designTokens.colors.gray[6]
                }}
              >
                총 예산
              </span>
            </div>

            {/* Budget Summary */}
            <div className="flex justify-center gap-2.5" style={{ flex: 1 }}>
              {/* Budget */}
              <div className="flex flex-col justify-center gap-2" style={{ width: '120px' }}>
                <div style={{ width: '120px' }}>
                  <span 
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '14px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    예산 (단위: 원)
                  </span>
                </div>
                <div className="flex items-center p-3" style={{ width: '100%', height: 'auto' }}>
                  <span 
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    {formatNumber(totals.budget)}원
                  </span>
                </div>
              </div>

              {/* Execution */}
              <div className="flex flex-col justify-center gap-2" style={{ width: '120px' }}>
                <div style={{ width: '120px' }}>
                  <span 
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '14px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    집행 (단위: 원)
                  </span>
                </div>
                <div className="flex items-center p-3" style={{ width: '100%', height: 'auto' }}>
                  <span 
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    {formatNumber(totals.execution)}원
                  </span>
                </div>
              </div>

              {/* Execution Rate */}
              <div className="flex flex-col justify-center gap-2" style={{ width: '120px' }}>
                <div style={{ width: '120px' }}>
                  <span 
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '14px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    집행율
                  </span>
                </div>
                <div className="flex items-center p-3" style={{ width: '100%', height: 'auto' }}>
                  <span 
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    {totals.rate}%
                  </span>
                </div>
              </div>
            </div>

            {/* Add Button */}
            <div className="flex justify-center items-center gap-1 px-2 py-0" style={{ width: 'auto', height: '28px' }}>
              <button
                type="button"
                onClick={handleAddItem}
                disabled={disabled}
                className="flex items-center gap-1 px-2 py-0 rounded transition-colors"
                style={{
                  backgroundColor: disabled ? designTokens.colors.gray[1] : designTokens.colors.gray[1],
                  color: disabled ? designTokens.colors.gray[3] : designTokens.colors.gray[6],
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  fontWeight: 600,
                  border: `2px solid ${designTokens.colors.gray[1]}`,
                  borderRadius: '4px',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  height: '28px'
                }}
              >
                <span style={{ fontSize: '14px', lineHeight: '14px' }}>+</span>
                예산 추가
              </button>
            </div>
          </div>

          {/* Budget Items */}
          {budgetData.items.length > 0 && (
            <div className="space-y-1.5">
              {budgetData.items.map((item) => (
                <div key={item.id} className="flex justify-center items-center gap-2.5">
                  {/* Tree Indicator and Budget Name */}
                  <div className="flex items-center gap-1.5 px-0 py-0" style={{ paddingLeft: '10px', width: 'auto' }}>
                    <span 
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '14px',
                        fontWeight: 400,
                        color: designTokens.colors.gray[6]
                      }}
                    >
                      ㄴ
                    </span>
                    <div className="flex flex-col justify-center gap-2" style={{ width: '150px' }}>
                      <div style={{ width: '120px' }}>
                        <div className="flex items-center gap-0.5">
                          <span 
                            style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: '12px',
                              fontWeight: 400,
                              color: designTokens.colors.gray[6]
                            }}
                          >
                            예산명
                          </span>
                          <span style={{ color: designTokens.colors.error.DEFAULT }}>*</span>
                        </div>
                      </div>
                      <div className="flex flex-col justify-center gap-2" style={{ width: '100%' }}>
                        <div className="flex items-center gap-1.5 px-3 py-0 border rounded" style={{ width: '100%', height: '32px', backgroundColor: disabled ? designTokens.colors.gray[0] : 'white', borderColor: errors[`${item.id}_name`] ? designTokens.colors.error.DEFAULT : designTokens.colors.gray[1] }}>
                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => handleItemChange(item.id, 'name', e.target.value)}
                            placeholder="예산명 입력"
                            disabled={disabled}
                            className="w-full border-none outline-none bg-transparent"
                            style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: '14px',
                              color: designTokens.colors.gray[6]
                            }}
                          />
                        </div>
                        {errors[`${item.id}_name`] && (
                          <span style={{ fontSize: '12px', color: designTokens.colors.error.DEFAULT }}>
                            {errors[`${item.id}_name`]}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Budget Amount */}
                  <div className="flex flex-col justify-center gap-2" style={{ width: '120px' }}>
                    <div style={{ width: '120px' }}>
                      <div className="flex items-center gap-0.5">
                        <span 
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: '12px',
                            fontWeight: 400,
                            color: designTokens.colors.gray[6]
                          }}
                        >
                          목표
                        </span>
                        <span style={{ color: designTokens.colors.error.DEFAULT }}>*</span>
                      </div>
                    </div>
                    <div className="flex flex-col justify-center gap-2" style={{ width: '100%' }}>
                      <div className="flex items-center gap-1.5 px-3 py-0 border rounded" style={{ width: '100%', height: '32px', backgroundColor: disabled ? designTokens.colors.gray[0] : 'white', borderColor: errors[`${item.id}_budget`] ? designTokens.colors.error.DEFAULT : designTokens.colors.gray[1] }}>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={getDisplayValue(item.id, 'budget', item.budget)}
                          onFocus={() => handleInputFocus(item.id, 'budget', item.budget)}
                          onChange={(e) => handleInputChange(item.id, 'budget', e.target.value)}
                          onBlur={(e) => handleInputBlur(item.id, 'budget', e.target.value)}
                          placeholder="목표값 입력 (소수점 가능)"
                          disabled={disabled}
                          className="w-full border-none outline-none bg-transparent"
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: '14px',
                            color: designTokens.colors.gray[6]
                          }}
                        />
                      </div>
                      {errors[`${item.id}_budget`] && (
                        <span style={{ fontSize: '12px', color: designTokens.colors.error.DEFAULT }}>
                          {errors[`${item.id}_budget`]}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Execution Amount */}
                  <div className="flex flex-col justify-center gap-2" style={{ width: '120px' }}>
                    <div style={{ width: '120px' }}>
                      <span 
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '12px',
                          fontWeight: 400,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        달성
                      </span>
                    </div>
                    <div className="flex flex-col justify-center gap-2" style={{ width: '100%' }}>
                      <div className="flex items-center gap-1.5 px-3 py-0 border rounded" style={{ width: '100%', height: '32px', backgroundColor: disabled ? designTokens.colors.gray[0] : 'white', borderColor: designTokens.colors.gray[1] }}>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={getDisplayValue(item.id, 'execution', item.execution)}
                          onFocus={() => handleInputFocus(item.id, 'execution', item.execution)}
                          onChange={(e) => handleInputChange(item.id, 'execution', e.target.value)}
                          onBlur={(e) => handleInputBlur(item.id, 'execution', e.target.value)}
                          placeholder="달성값 입력 (소수점 가능)"
                          disabled={disabled}
                          className="w-full border-none outline-none bg-transparent"
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: '14px',
                            color: designTokens.colors.gray[6]
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Delete Button */}
                  <div className="flex flex-col justify-end items-center" style={{ width: 'auto', height: '100%' }}>
                    <div className="flex justify-center items-center gap-1 px-2.5 py-0 border rounded transition-colors" style={{ width: 'auto', height: '34px', backgroundColor: disabled ? designTokens.colors.gray[1] : designTokens.colors.gray[1], borderColor: designTokens.colors.gray[1], cursor: disabled ? 'not-allowed' : 'pointer' }}>
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(item.id)}
                        disabled={disabled}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: disabled ? designTokens.colors.gray[3] : designTokens.colors.gray[6],
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '16px',
                          fontWeight: 600,
                          cursor: disabled ? 'not-allowed' : 'pointer'
                        }}
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BudgetSection